/**
 * @fileoverview Componente para retornar órdenes activas a edición
 * Permite devolver órdenes que están activas pero no tienen plan asignado
 */

import React, { useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert, Spinner } from 'reactstrap';
import { UncontrolledTooltip } from 'reactstrap';
import { NotaRepository } from '../repositories/NotaRepository';
import { OrdenProduccionRepository } from '../repositories/OrdenProduccionRepository';
import { createModuleLogger } from '../utils/logger';

interface ReturnOrderButtonProps {
  orden: {
    id: string;
    numero: number;
    nombre_producto: string;
    activa: string | null; // log_estado ID if active
    tarea: string | null; // tarea_orden ID if has assigned plan
  };
  onReturnOrder: (orderId: string) => void;
  onShowAlert: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
}

const ReturnOrderButton: React.FC<ReturnOrderButtonProps> = ({
  orden,
  onReturnOrder,
  onShowAlert,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [note, setNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  
  // Crear logger para este componente
  const componentLogger = createModuleLogger('ReturnOrderButton');
  
  // Repositorios
  const notaRepository = new NotaRepository();
  const ordenProduccionRepository = new OrdenProduccionRepository();
  
  // Obtener usuario del localStorage
  const getCurrentUser = () => {
    try {
      const authUser = localStorage.getItem("authUser");
      if (authUser) {
        const userData = JSON.parse(authUser);
        return userData.record;
      }
      return null;
    } catch (error) {
      componentLogger.error('Error al obtener usuario del localStorage', error);
      return null;
    }
  };

  // Verificar si la orden puede ser retornada
  const canReturnOrder = orden.activa && orden.activa.trim() !== '' && !orden.tarea;

  // Función para abrir el modal
  const handleOpenModal = () => {
    if (canReturnOrder) {
      setIsModalOpen(true);
      setNote('');
      setValidationError(null);
    }
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNote('');
    setValidationError(null);
  };

  // Función para validar la nota
  const validateNote = (noteText: string): string | null => {
    if (!noteText || noteText.trim().length === 0) {
      return 'La nota es requerida';
    }
    if (noteText.trim().length < 10) {
      return 'La nota debe tener al menos 10 caracteres';
    }
    return null;
  };

  // Función para retornar la orden
  const handleReturnOrder = async () => {
    try {
      setIsProcessing(true);
      setValidationError(null);

      // Validar la nota
      const noteError = validateNote(note);
      if (noteError) {
        setValidationError(noteError);
        return;
      }

      // Obtener usuario actual
      const currentUser = getCurrentUser();
      if (!currentUser) {
        throw new Error('Usuario no autenticado');
      }

      componentLogger.info('Iniciando proceso de retorno de orden', {
        orderId: orden.id,
        userId: currentUser.id,
      });

      // Crear la nota
      const createdNote = await notaRepository.createNote({
        nota: note.trim(),
        usuario: currentUser.id,
        orden_produccion: orden.id,
      });

      componentLogger.info('Nota creada exitosamente', {
        notaId: createdNote.id,
        orderId: orden.id,
      });

      // Eliminar el registro de log_estado que está en la relación activa
      if (orden.activa) {
        await ordenProduccionRepository.deleteLogEstado(orden.activa);
      }

      componentLogger.info('Orden actualizada exitosamente', {
        orderId: orden.id,
      });

      // Cerrar modal y notificar éxito
      handleCloseModal();
      onReturnOrder(orden.id);
      onShowAlert('success', 'Orden retornada a edición exitosamente');

    } catch (error: any) {
      componentLogger.error('Error al retornar la orden', {
        error: error.message,
        orderId: orden.id,
      });
      
      onShowAlert('error', `Error al retornar la orden: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Si la orden no puede ser retornada, no renderizar el botón
  if (!canReturnOrder) {
    return null;
  }

  return (
    <>
      {/* Botón principal */}
      <Button 
        id={`retornar-${orden.id}`}
        color="warning" 
        size="sm" 
        className="me-1 px-2 py-1" 
        onClick={handleOpenModal}
        title="Retornar Orden a Edición"
        data-testid="return-order-btn"
      >
        <i className="fas fa-undo me-1" data-testid="return-icon"></i>
        Retornar Orden
      </Button>
      
      {/* Tooltip */}
      <UncontrolledTooltip placement="top" target={`retornar-${orden.id}`}>
        Retornar Orden
      </UncontrolledTooltip>

      {/* Modal de confirmación */}
      <Modal isOpen={isModalOpen} toggle={handleCloseModal} size="md" centered>
        <ModalHeader toggle={handleCloseModal} data-testid="return-order-modal">
          <i className="fas fa-undo text-warning me-2"></i>
          Retornar Orden a Edición
        </ModalHeader>
        
        <ModalBody>
          <div className="text-center mb-3">
            <i className="fas fa-question-circle text-warning" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3">¿Está seguro?</h5>
            <p className="text-muted">
              Está a punto de retornar la orden <strong>#{orden.numero}</strong> - {orden.nombre_producto} a estado de edición.
            </p>
            <p className="text-muted">
              Esta acción eliminará el estado activo de la orden y permitirá editarla nuevamente.
            </p>
          </div>

          <div className="alert alert-info">
            <h6>Información de la Orden:</h6>
            <ul className="mb-0">
              <li><strong>Número:</strong> #{orden.numero}</li>
              <li><strong>Producto:</strong> {orden.nombre_producto}</li>
              <li><strong>Estado:</strong> Activa (sin plan asignado)</li>
            </ul>
          </div>

          <div className="mb-3">
            <label htmlFor="note-textarea" className="form-label">
              <strong>Nota explicativa <span className="text-danger">*</span></strong>
            </label>
            <textarea
              id="note-textarea"
              className={`form-control ${validationError ? 'is-invalid' : ''}`}
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Explique por qué se está retornando la orden a edición..."
              data-testid="note-textarea"
            />
            {validationError && (
              <div className="invalid-feedback" data-testid="validation-error">
                {validationError}
              </div>
            )}
          </div>

          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle"></i>
            <strong> Atención:</strong> Esta acción no se puede deshacer. 
            La orden volverá al estado de edición y se creará un registro de nota.
          </div>
        </ModalBody>
        
        <ModalFooter>
          <Button 
            color="secondary" 
            onClick={handleCloseModal}
            disabled={isProcessing}
            data-testid="cancel-return-btn"
          >
            Cancelar
          </Button>
          <Button 
            color="warning" 
            onClick={handleReturnOrder}
            disabled={isProcessing}
            data-testid="confirm-return-btn"
          >
            {isProcessing ? (
              <>
                <Spinner size="sm" className="me-2" data-testid="loading-spinner" />
                Procesando...
              </>
            ) : (
              <>
                <i className="fas fa-undo me-1"></i>
                Retornar Orden
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ReturnOrderButton;
